import 'mapbox-gl/dist/mapbox-gl.css'

import { Alert, Row, Spin } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import {
  useBufferedFilteredEntries,
  useFilterToSites,
  useVersionAwareDrilldownPanelData,
} from 'features/drilldown'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import React, {
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  LngLatBoundsLike,
  Map as MapboxMap,
  MapRef,
  Marker,
  Popup,
} from 'react-map-gl'
import styled from 'styled-components'
import { mapStyle as defaultMapboxStyle } from 'styles/mapBox'
import { GenericPanelData, RolodexEntryType } from 'types'

import {
  MapboxAreaSelect,
  SelectablePoint,
  SerializableLatLngBounds,
} from './MapboxAreaSelect'

interface MapPanelData extends GenericPanelData {
  selectedMapArea: SerializableLatLngBounds | null
}

const LATEST_DATA_SCHEMA_VERSION = 3

export const DEFAULT_MAP_PANEL_DATA: MapPanelData = {
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  selectedMapArea: null,
  title: 'Site Map',
  isPinned: true,
}

const PIN_COLOR = '#f55238'

const PinContainer = styled.div`
  width: 26px;
  height: 34px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
`

const PinCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${PIN_COLOR};
  border: 2px solid #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
`

const PinCircleInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #fff;
`

const PinPointer = styled.div`
  width: 16px;
  height: 16px;
  background-color: ${PIN_COLOR};
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: translate(-50%, 0) rotate(45deg);
  position: absolute;
  top: 16px;
  left: 50%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.35);
`

const MapPinMarker: FC<{ onMouseEnter?: () => void }> = ({ onMouseEnter }) => (
  <PinContainer onMouseEnter={onMouseEnter}>
    <PinCircle>
      <PinCircleInner />
    </PinCircle>
    <PinPointer />
  </PinContainer>
)

const PopupLabel = styled.div`
  color: #1b1a17;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 4px;
`

export interface DrilldownSiteLocationsMapProps {
  mapStyle?: React.CSSProperties
  onHoverSiteMarker?: (siteId: string) => void
  onClickSiteMarker?: (siteId: string) => void
}

/**
 * A Drilldown component to show locations on a map, and allow a user to
 * select an area, adding the selected points to the context as a filter.
 * @param props
 * @returns component
 */
const MapComponent: FC<DrilldownSiteLocationsMapProps> = ({
  mapStyle,
  onHoverSiteMarker,
  onClickSiteMarker,
}) => {
  const { MAPBOX_KEY } = useRuntimeVariables()
  const mapRef = useRef<MapRef>(null)
  const [sitesWithinBounds, setSitesWithinBounds] = useState<
    ReadonlyArray<string>
  >([])
  const [activePopupSiteId, setActivePopupSiteId] = useState<string | null>(
    null
  )

  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<MapPanelData>()

  const { selectedMapArea } = panelData

  const { data: sites, isLoading } = useBufferedFilteredEntries({
    entryType: RolodexEntryType.SITE,
    dataKey: 'sites',
  })

  const sitesToFilterTo = useMemo(() => {
    return selectedMapArea ? new Set(sitesWithinBounds) : null
  }, [selectedMapArea, sitesWithinBounds])

  useFilterToSites(sitesToFilterTo)

  const siteLocations: ReadonlyArray<SelectablePoint> = useMemo(
    () =>
      sites
        .map((site) => {
          const coordinates = site.data.coordinates
          return coordinates
            ? {
                id: site.id,
                lat: coordinates[0],
                lng: coordinates[1],
              }
            : null
        })
        .filter((point): point is SelectablePoint => !!point),
    [sites]
  )

  const activePopupLocation = useMemo(
    () =>
      activePopupSiteId
        ? (siteLocations.find(
            (location) => location.id === activePopupSiteId
          ) ?? null)
        : null,
    [activePopupSiteId, siteLocations]
  )
  const activePopupSite = useMemo(
    () =>
      activePopupSiteId
        ? (find(sites, (site) => site.id === activePopupSiteId) ?? null)
        : null,
    [activePopupSiteId, sites]
  )

  const mapContainerStyle = useMemo(
    () => ({
      height: '500px',
      width: '100%',
      ...(mapStyle ?? {}),
    }),
    [mapStyle]
  )

  const setSelectedMapArea = useCallback(
    (val: SetStateAction<SerializableLatLngBounds | null>) => {
      setPanelData((prev) => {
        const newVal =
          typeof val === 'function' ? val(prev.selectedMapArea) : val
        if (isEqual(newVal, prev.selectedMapArea)) {
          return prev
        }
        return {
          ...prev,
          selectedMapArea: newVal,
        }
      })
    },
    [setPanelData]
  )

  const isPointInDrawnBounds = useCallback(
    ([lat, lng]: [number, number]) => {
      if (!selectedMapArea) {
        return true
      }
      const { northEast, southWest } = selectedMapArea
      return (
        lat >= southWest.lat &&
        lat <= northEast.lat &&
        lng >= southWest.lng &&
        lng <= northEast.lng
      )
    },
    [selectedMapArea]
  )

  useEffect(() => {
    if (siteLocations && siteLocations.length) {
      const selectedPointIds = siteLocations
        .filter(({ lat, lng }) => isPointInDrawnBounds([lat, lng]))
        .map(({ id }) => id)

      setSitesWithinBounds((prev) =>
        isEqual(selectedPointIds, prev) ? prev : selectedPointIds
      )
    }
  }, [isPointInDrawnBounds, siteLocations, selectedMapArea])

  const fitMapToSiteLocations = useCallback(() => {
    if (!mapRef.current || siteLocations.length === 0) {
      return
    }
    const latitudes = siteLocations.map((location) => location.lat)
    const longitudes = siteLocations.map((location) => location.lng)
    const minLat = Math.min(...latitudes)
    const maxLat = Math.max(...latitudes)
    const minLng = Math.min(...longitudes)
    const maxLng = Math.max(...longitudes)
    const latDelta = Math.max(maxLat - minLat, 0.1)
    const lngDelta = Math.max(maxLng - minLng, 0.1)
    const bounds: LngLatBoundsLike = [
      minLng - lngDelta * 0.1,
      minLat - latDelta * 0.1,
      maxLng + lngDelta * 0.1,
      maxLat + latDelta * 0.1,
    ]
    requestAnimationFrame(() => {
      mapRef.current?.fitBounds(bounds, { padding: 60, duration: 0 })
    })
  }, [siteLocations])

  useEffect(() => {
    fitMapToSiteLocations()
  }, [fitMapToSiteLocations])

  if (isLoading) {
    return (
      <Row justify='center' align='middle' style={{ height: '50px' }}>
        <Spin />
      </Row>
    )
  }

  if (siteLocations.length === 0) {
    return (
      <Alert
        title='There needs to be at least one site with valid coordinates in scope to show a site map.'
        type='info'
        showIcon
      />
    )
  }

  return (
    <MapboxMap
      ref={mapRef}
      mapboxAccessToken={MAPBOX_KEY}
      style={mapContainerStyle}
      mapStyle={defaultMapboxStyle}
      onLoad={fitMapToSiteLocations}
      initialViewState={{
        latitude: 0,
        longitude: 0,
        zoom: 1,
      }}>
      <MapboxAreaSelect
        mapRef={mapRef}
        selectedMapArea={selectedMapArea}
        setSelectedMapArea={setSelectedMapArea}
        onClearSelection={() => setActivePopupSiteId(null)}
      />
      {siteLocations.map(({ id, lat, lng }) => {
        const site = find(sites, (s) => s.id === id)
        return site ? (
          <Marker
            key={id}
            latitude={lat}
            longitude={lng}
            anchor='bottom'
            onClick={(event) => {
              event.originalEvent.stopPropagation?.()
              setActivePopupSiteId(site.id)
              onClickSiteMarker?.(site.id)
            }}>
            <MapPinMarker onMouseEnter={() => onHoverSiteMarker?.(site.id)} />
          </Marker>
        ) : (
          <></>
        )
      })}
      {activePopupSite && activePopupLocation && (
        <Popup
          anchor='bottom'
          latitude={activePopupLocation.lat}
          longitude={activePopupLocation.lng}
          closeButton={false}
          closeOnMove={false}
          onClose={() => setActivePopupSiteId(null)}>
          <PopupLabel>{activePopupSite.data.name ?? 'Unnamed Site'}</PopupLabel>
        </Popup>
      )}
    </MapboxMap>
  )
}

export const DrilldownSiteLocationsMap = React.memo(MapComponent)
